import { computeContentDiff, computeFieldDiffs } from "./briefDiffUtils";
import type { Version } from "./briefTypes";
import { DiffSpan } from "./DiffSpan";

interface BriefVersionDiffProps {
  oldVersion: Version;
  newVersion: Version;
}

export function BriefVersionDiff({ oldVersion, newVersion }: BriefVersionDiffProps) {
  const notesChanges = computeContentDiff(
    oldVersion.additionalNotes ?? "",
    newVersion.additionalNotes ?? "",
  );
  const fieldDiffs = computeFieldDiffs(oldVersion, newVersion);
  const hasNotesChanges = notesChanges.some((c) => c.added || c.removed);
  const hasChanges = hasNotesChanges || fieldDiffs.length > 0;

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-medium text-text-secondary">
        Changes from v{oldVersion.versionNumber} to v{newVersion.versionNumber}
      </h4>

      {!hasChanges && <p className="text-xs text-text-muted">No changes</p>}

      {hasNotesChanges && (
        <div className="rounded bg-card-hover p-3 text-sm leading-relaxed text-text-primary">
          <p className="mb-1 text-xs font-medium text-text-secondary">Additional notes</p>
          {notesChanges.map((change) => (
            <DiffSpan key={change.value} change={change} />
          ))}
        </div>
      )}

      {fieldDiffs.length > 0 && (
        <div className="space-y-1">
          <h5 className="text-xs font-medium text-text-secondary">Field changes</h5>
          {fieldDiffs.map((diff) => (
            <div key={diff.label} className="text-xs text-text-muted">
              <span className="font-medium">{diff.label}:</span>{" "}
              {diff.oldValue ? (
                <span className="bg-red-100 text-red-800 line-through dark:bg-red-900/30 dark:text-red-400">
                  {diff.oldValue}
                </span>
              ) : (
                <span className="italic text-text-faint">empty</span>
              )}
              {" → "}
              {diff.newValue ? (
                <span className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  {diff.newValue}
                </span>
              ) : (
                <span className="italic text-text-faint">empty</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
